import React, { FC } from "react";
import {
  Divider,
  Drawer,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Toolbar,
  CssBaseline,
  AppBar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ImageBanner from "../ImageBanner/ImageBanner";
import DoorTabs from "../DoorTabs/DoorTabs";
import RequiredOptionsDisplay from "../requiredDisplayed/RequiredOptionsDisplay";
import RequiredDisplayBifoldCorner from "../requiredDisplayed/RequiredDisplayBifoldCorner";
import RequiredDisplayPivot from "../requiredDisplayed/RequiredDisplayPivot";
import RequiredDisplayBifoldCombo from "../requiredDisplayed/RequiredDisplayBifoldCombo";
import Spinner from "../Spinner";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import AccountDropdown from "../AccountDropdown/AccountDropdown";
import ExpiredModal from "../ExpiredModal";
import InfoAlert from "../InfoAlert";
import Skeleton from "@mui/material/Skeleton";
import CollapsePriceInfo from "../CollapsePrice/CollapsePriceInfo";
import CartBadge from "../CartBadge";
import QuoteNumber from "../QuoteNumber";
import OtsmIcon from "../OtsmIcon";
import SidebarTotals from "../SidebarTotals";
import QuoteSummaryBtn from "../QuoteSummaryBtn";
import DragDropList from "../DragDropList/DragDropList";
import SidebarSkeleton from "../SidebarSkeleton";
import UnitCheckModal from "../UnitCheckModal/UnitCheckModal";
import CartCheckModal from "../CartCheck/CartCheckModal";
import Copyright from "../Copyright";
import { blueGrey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import useSidebarSelector from "./useSidebarSelector";
import dealerRoles from "../../util/dealerAccounts";

const StyledDrawerDiv = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "200vh",
  overflow: "hidden",
});

const StyledButton = styled(Button)(({ theme }) => ({
  color: "black",
  width: 143,
  height: 32,
  marginTop: 10,
}));

const StyledDivRoot = styled("div")({
  display: "flex",
  minHeight: "100vh",
  paddingBottom: "2rem",
  position: "relative",
  backgroundColor: "#fafafa",
  maxWidth: "100%",
});

const drawerWidthSidebar = 325;

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== "isDrawerCollapsed",
})(({ isDrawerCollapsed }: { isDrawerCollapsed: boolean }) => ({
  width: isDrawerCollapsed ? "" : `calc(100% - ${drawerWidthSidebar}px)`,
  marginLeft: isDrawerCollapsed ? 0 : drawerWidthSidebar,
  display: "flex",
}));

const StyledNav = styled("nav", {
  shouldForwardProp: (prop) => prop !== "isDrawerCollapsed",
})(({ isDrawerCollapsed }: { isDrawerCollapsed: boolean }) => ({
  width: isDrawerCollapsed ? "" : drawerWidthSidebar,
  flexShrink: isDrawerCollapsed ? 1 : 0,
}));

const StyledToolbar = styled(Toolbar)({
  backgroundColor: "#4b4b4b",
  display: "flex",
  justifyContent: "space-between",
});

const StyledFlexStartDiv = styled("div")({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  width: "50%",
});

const StyledFlexEndDiv = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "50%",
});

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "isDrawerCollapsed",
})(
  ({
    theme,
    isDrawerCollapsed,
  }: {
    isDrawerCollapsed: boolean;
    theme?: any;
  }) => ({
    marginRight: theme.spacing(2),
    display: isDrawerCollapsed ? "block" : "none",
    position: "absolute",
    left: 20,
  }),
);

const StyledImgLogoSmall = styled("img")({
  height: 75,
  width: 300,
});

const StyledImgLogo = styled("img")({
  height: 80,
  width: 60,
  marginLeft: 50,
});

const StyledMain = styled("main")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3),
  justifyContent: "center",
  alignItems: "center",
  flexBasis: "100%",
  paddingTop: "2rem",
  borderBottom: "1px solid #eeeeee",
}));

const StyledDiv = styled("div")({
  position: "relative",
  height: 75,
  borderBottom: "1px solid #cdd1d3",
});

const StyledDiv2 = styled("div")({
  position: "relative",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
});

const StyledList = styled(List)({
  zIndex: 10,
  backgroundColor: blueGrey[50],
});

const StyledDrawer = styled(Drawer)({
  width: drawerWidthSidebar,
  backgroundColor: blueGrey[50],
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    width: drawerWidthSidebar,
    backgroundColor: blueGrey[50],
  },
  "& .MuiPaper-root": {
    zIndex: 0,
  },
});

const SideBar: FC = () => {
  const {
    sidebarTotals,
    loadDuplicate,
    windowOrDoor,
    handleSwitchChange,
    checkedPD,
    routeToOrderSummary,
    handleAdminFactor,
    changeQuantity,
    handleResumeLater,
    backToFormPage,
    handleDrawerToggle,
    resetAllValues,
    duplicateUnit,
    addDoorToSidebar,
    adminFactor,
    isLoading,
    loadingQuantity,
    handleClose,
    open,
    modalText,
    handleCloseCCM,
    handleCloseCommon,
    openCCM,
    cartCheck,
    openCommon,
    isSubmitBtnPressed,
    editQuote,
    setEditQuote,
    nonMatches,
    data,
    isFetching,
    userData,
    fetchingUser,
    theme,
    orderedUnits,
    setOrder,
    setWindowOrDoor,
    isEdit,
    mobileOpen,
    doorType,
    userId,
    isDrawerCollapsed,
    canEditCustomCladColorAndFreightPrice
  } = useSidebarSelector();

  const drawer = (
    <StyledDrawerDiv>
      <StyledDiv>
        <StyledDiv2>
          <StyledButton
            onClick={backToFormPage}
            variant="contained"
            size="small"
            color="cyan"
          >
            <NavigateBeforeIcon style={{ display: "inline" }} />
            Back to Form
          </StyledButton>

          {!fetchingUser && !isFetching && data?.quote.units.length ? (
            <CartBadge numOfItems={data.quote.totalUnits} />
          ) : null}
        </StyledDiv2>
      </StyledDiv>

      {!fetchingUser &&
      !isFetching &&
      !loadingQuantity &&
      data?.quote.units.length ? (
        <DragDropList
          quoteId={data.quote._id}
          status={data.quote.status}
          units={orderedUnits}
          setUnits={setOrder}
          loadDuplicate={loadDuplicate}
          duplicateUnit={duplicateUnit}
          setWindowOrDoor={setWindowOrDoor}
          resetAllValues={resetAllValues}
          setEditQuote={setEditQuote}
          checkedPD={checkedPD}
          changeQuantity={changeQuantity}
          dealer={data.quote.dealer}
          dealerEmail={data.quote.email}
          // setWindowVal={setWindowVal}
        />
      ) : (
        <SidebarSkeleton
          salesperson={data?.quote.salesperson}
          fetchingUser={fetchingUser}
          isFetching={isFetching}
          loadingQuantity={loadingQuantity}
        />
      )}

      <StyledList>
        <Divider />

        {!fetchingUser &&
        !loadingQuantity &&
        (!userData.otsm || !dealerRoles.includes(userData.signInRole)) ? (
          <CollapsePriceInfo
            handleChange={handleSwitchChange}
            checked={checkedPD}
          >
            {!isFetching &&
              sidebarTotals.map((text, index) => (
                <div key={index}>
                  <SidebarTotals
                    quoteId={data?.quote?._id}
                    text={text}
                    customClad={data?.quote.customCladPrice || 0}
                    splitFinish={data?.quote.splitFinishPrice || 0}
                    factor={data?.quote.factor}
                    canEditFactor={
                      userData.factor === 1 || userData.signInRole === "Admin"
                    }
                    canEditCustomCladColorAndFreightPrice={
                      canEditCustomCladColorAndFreightPrice
                    }
                    dealerTax={userData.dealerTax || null}
                    handleAdminFactor={handleAdminFactor}
                    adminFactor={adminFactor}
                    suggRetailPrice={data?.quote.suggestedRetailPrice}
                    grandTotal={data?.quote.grandTotal}
                    deliveryCharge={data?.quote.deliveryCharge}
                    freightQuote={data?.quote.freightQuote || {}}
                    adminCharge={
                      userData.dealerTax && data?.quote.adminCharge
                        ? data.quote.adminCharge
                        : null
                    }
                    customCladColorOverMaxApplies={
                      data?.quote.customCladColorOverMaxApplies || false
                    }
                    customCladColorPriceUpdateRequired={
                      data?.quote.customCladColorPriceUpdateRequired || false
                    }
                    freightQuoteError={
                      data?.quote.freightQuote?.freightQuoteError || false
                    }
                    freightQuotePriceUpdateRequired={
                      data?.quote.freightQuote
                        ?.freightQuotePriceUpdateRequired || false
                    }
                  />
                </div>
              ))}
          </CollapsePriceInfo>
        ) : !fetchingUser && userData.otsm ? null : (
          <ListItem>
            <ListItemText
              primary={<Skeleton variant="rectangular" height={50} />}
            />
          </ListItem>
        )}

        {!fetchingUser && !isFetching && !loadingQuantity ? (
          <QuoteSummaryBtn
            grandTotal={data?.quote.grandTotal}
            disableQSum={data?.disableQSum}
            editBtn={isEdit || false}
            routeToOrderSummary={routeToOrderSummary}
          />
        ) : (
          <ListItem>
            <ListItemText
              primary={<Skeleton variant="rectangular" height={50} />}
            />
          </ListItem>
        )}
      </StyledList>
    </StyledDrawerDiv>
  );

  return (
    <StyledDivRoot>
      <CssBaseline />
      <StyledAppBar position="fixed" isDrawerCollapsed={isDrawerCollapsed}>
        <StyledToolbar>
          <StyledFlexStartDiv>
            <StyledIconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              size="large"
              isDrawerCollapsed={isDrawerCollapsed}
            >
              <MenuIcon />
            </StyledIconButton>

            {!isDrawerCollapsed ? (
              <StyledImgLogoSmall
                src="https://res.cloudinary.com/ag-millworks/image/upload/AG_Logos/AGlogoSmall.png"
                alt="Company Logo"
              />
            ) : (
              <StyledImgLogo
                src="https://res.cloudinary.com/ag-millworks/image/upload/doorlogo.png"
                alt=""
              />
            )}
          </StyledFlexStartDiv>

          <StyledFlexEndDiv>
            {!fetchingUser &&
              userData.otsm &&
              dealerRoles.includes(userData.signInRole) && <OtsmIcon />}

            {!isFetching && data && (
              <div style={{ paddingRight: 10 }}>
                <QuoteNumber quoteNumber={data.quote.quoteNumber} />
              </div>
            )}

            <Button
              variant="contained"
              size="small"
              color="cyan"
              style={{ color: "white" }}
              onClick={handleResumeLater}
            >
              Resume Later
            </Button>

            <AccountDropdown
              color="white"
              resumeLaterBtn={true}
              returnedPath="config"
            />
          </StyledFlexEndDiv>
        </StyledToolbar>
      </StyledAppBar>

      <StyledNav isDrawerCollapsed={isDrawerCollapsed}>
        {isDrawerCollapsed ? (
          <StyledDrawer
            variant="temporary"
            anchor={theme.direction === "rtl" ? "right" : "left"}
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            {drawer}
          </StyledDrawer>
        ) : (
          <StyledDrawer variant="permanent" open>
            {drawer}
          </StyledDrawer>
        )}
      </StyledNav>

      <StyledMain style={{ maxWidth: !doorType ? "100%" : "" }}>
        <Spinner isLoading={isLoading} />

        <ExpiredModal
          open={open}
          handleClose={handleClose}
          modalText={modalText}
          changeDoor={false}
        />

        {!isFetching && data && cartCheck.length ? (
          <CartCheckModal
            cartCheck={cartCheck}
            handleClose={handleCloseCCM}
            open={openCCM}
            routeToOrderSummary={routeToOrderSummary}
            quoteId={data.quote._id}
            userId={userId}
          />
        ) : null}

        {!isFetching && data !== null && data !== undefined && (
          <UnitCheckModal
            open={openCommon}
            handleClose={handleCloseCommon}
            nonMatches={nonMatches}
            addToCart={addDoorToSidebar}
          />
        )}

        {doorType && (
          <>
            {!isEdit && <InfoAlert />}

            <ImageBanner />

            {doorType === "Bi-fold Zero Corner" ? (
              <RequiredDisplayBifoldCorner />
            ) : doorType === "Bi-fold Door/Window Combo" ? (
              <RequiredDisplayBifoldCombo />
            ) : doorType === "Pivot" ? (
              <RequiredDisplayPivot />
            ) : (
              <RequiredOptionsDisplay />
            )}
          </>
        )}
        <DoorTabs
          windowOrDoor={windowOrDoor}
          setWindowOrDoor={setWindowOrDoor}
          resetAllValues={resetAllValues}
          editQuote={editQuote}
          submitPressed={isSubmitBtnPressed}
          addDoorToSidebar={addDoorToSidebar}
          dealer={data?.quote?.dealer || ""}
          email={data?.quote?.email || ""}
          // setWindowVal={setWindowVal}
          // windowVal={windowVal}
        />
      </StyledMain>

      <Copyright position="center1" />
    </StyledDivRoot>
  );
};

export default SideBar;
